import { useCallback, useState } from "react";
import { message } from "antd";

import { Handbook } from "features/handbook/model/handbook";
import {
  useAddHandbookMutation,
  useEditHandbookMutation,
  useRemoveMutation,
} from "features/handbook/api/handbookApi";
import { useConfirmDelete } from "shared/hooks/useConfirmDelete";

type Args = {
  dictCode?: string;
  /** Перезапрос списка текущего сервиса после изменения. */
  reloadCurrent: () => unknown;
};

/** Создание, редактирование и удаление записей справочника вместе с состоянием формы. */
export function useHandbookCrud({ dictCode, reloadCurrent }: Args) {
  const confirmDelete = useConfirmDelete();

  const [add, { isLoading: isAdding }] = useAddHandbookMutation();
  const [edit, { isLoading: isEditing }] = useEditHandbookMutation();
  const [remove] = useRemoveMutation();

  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState<Handbook | null>(null);

  const openCreate = useCallback(() => {
    setEditingRow(null);
    setModalOpen(true);
  }, []);

  const openEdit = useCallback((row: Handbook) => {
    setEditingRow(row);
    setModalOpen(true);
  }, []);

  const closeModal = useCallback(() => setModalOpen(false), []);

  const handleSubmit = useCallback(
    async (payload: Partial<Handbook>) => {
      try {
        if (editingRow?.id) {
          await edit({
            slug: dictCode!,
            body: { ...payload, id: editingRow.id },
          }).unwrap();
          message.success("Запись обновлена");
        } else {
          await add({ slug: dictCode!, body: payload as Handbook }).unwrap();
          message.success("Запись создана");
        }
        setModalOpen(false);
        await reloadCurrent();
      } catch (e) {
        console.error(e);
        message.error("Не удалось сохранить");
      }
    },
    [add, edit, editingRow?.id, dictCode, reloadCurrent],
  );

  const handleDelete = useCallback(
    (row: Handbook) => {
      if (!dictCode) return;

      confirmDelete({
        content: row.nameRu || row.nameKy || `#${row.id}`,
        onOk: async () => {
          try {
            await remove({ slug: dictCode, id: row.id! }).unwrap();
            await reloadCurrent();
            message.success("Запись удалена");
          } catch {
            message.error("Не удалось удалить");
          }
        },
      });
    },
    [confirmDelete, remove, dictCode, reloadCurrent],
  );

  return {
    modalOpen,
    editingRow,
    openCreate,
    openEdit,
    closeModal,
    isSaving: isAdding || isEditing,
    handleSubmit,
    handleDelete,
  };
}
